// apps/web/src/hooks/briefing-story-context.ts
//
// KAN-661 U5 — the authenticated contextual read behind a briefing click.
// (Spec R17–R21, R25, R31, R32, R33, R35, R38, R39; flow F1 steps 9–10, F4.)
//
// ---------------------------------------------------------------------------
// WHY THIS IS NOT PART OF `useStory`
// ---------------------------------------------------------------------------
//
// `useStory` is shared, cacheable and identical for every reader: its key is the
// story id and nothing else. Contextual data is the opposite of all three — it
// is per-reader, per-token, and must not outlive the click that produced it. If
// the two shared a key, one reader's matches would be served to the next reader
// out of the same cache entry; if they shared a query, the ordinary story read
// would have to become uncacheable for everyone. Keeping them apart is what
// makes both properties true at once, and it is why `storyQueryKeys` is left
// untouched by this module (R14, R31).
//
// ---------------------------------------------------------------------------
// WHAT THE FIFTH KEY ELEMENT IS
// ---------------------------------------------------------------------------
//
// R31: "exact preference values and raw token are never cache-key material".
// The key still has to distinguish two different tokens for the same reader and
// story — two briefing links to the same story from different sends resolve to
// different clicked articles — so something derived from the token has to be in
// it. That something is a LOCAL digest: a fixed-width hash computed in this
// module, never exported, never sent anywhere, and not reversible to the token
// by anyone reading a devtools cache dump or a heap snapshot.
//
// It is deliberately NOT a cryptographic digest. `crypto.subtle` is async and a
// query key has to exist synchronously at render; more importantly the digest's
// job is partitioning, not authentication — nothing trusts it, the server
// verifies the token itself. FNV-1a run twice with different offset bases gives
// 64 bits of partition space with no collisions in any realistic set (a reader
// holds one, occasionally two, tokens at a time).
//
// ---------------------------------------------------------------------------
// WHY THE TOKEN IS HELD IN COMPONENT STATE AFTER BEING CONSUMED
// ---------------------------------------------------------------------------
//
// `consumePendingBriefingContext` is consume-once and clears `sessionStorage`
// on the first call (R38/R39) — a forwarded or re-opened link must not
// re-attach to a later visit. But R33 requires a preference mutation to
// INVALIDATE this query, and an invalidated query has to be able to refetch,
// which needs the token again. So the token lives in component state for the
// lifetime of this page view and dies with it: no storage, no module-level
// singleton, nothing that survives a reload or reaches another tab.
//
// ---------------------------------------------------------------------------
// WHAT NEVER HAPPENS HERE
// ---------------------------------------------------------------------------
//
// The token never enters a URL, a query string, a log line, an analytics
// payload, or a cache key. Exact match values arrive in the response body,
// render, and are never written anywhere else. Failure of any kind — 401, 500,
// a malformed body, a cancelled request — resolves to "no context", because F4
// requires a stale briefing link to land on an ordinary working story page
// rather than a broken one.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { API_BASE_URL } from '@/lib/api'
import {
  consumePendingBriefingContext,
  registerBriefingContextCacheReset,
  runBriefingContextRequest,
} from '@/lib/briefing-context-bootstrap'

/**
 * R4's reason vocabulary, mirrored on the reading side. Declared locally rather
 * than imported from `apps/api`: the web bundle must not depend on the API
 * package, and both are projections of the same four spec-level codes.
 */
export type BriefingMatchReason =
  | 'tracked_vendor'
  | 'keyword_watch'
  | 'industry'
  | 'jurisdiction'

export interface BriefingCurrentMatch {
  reason: BriefingMatchReason
  /** A human display value computed from the reader's CURRENT preferences (R18, R19). */
  displayValue: string
}

export interface BriefingStoryContext {
  articleId: string
  suggestedActions: string[]
  currentMatches: BriefingCurrentMatch[]
}

/** The API's bounded, fallback-safe envelope. `null` is a normal outcome (F4). */
export interface BriefingStoryContextResponse {
  context: BriefingStoryContext | null
}

const MATCH_REASONS: readonly BriefingMatchReason[] = [
  'tracked_vendor',
  'keyword_watch',
  'industry',
  'jurisdiction',
]

/**
 * The R31 key registry.
 *
 * `all` is the invalidation handle the preference/watch mutations in
 * `hooks/queries.ts` reach for, and the removal handle the auth teardown uses.
 * It is a PREFIX of every `detail` key, so one call covers every subject, story
 * and token this document holds — which is the point: on a subject change there
 * is no enumeration of "which contexts might exist" that could miss one.
 */
export const briefingStoryContextQueryKeys = {
  all: ['stories', 'briefing-context'] as const,
  detail: (subject: string, storyId: string, tokenDigest: string) =>
    ['stories', 'briefing-context', subject, storyId, tokenDigest] as const,
}

/**
 * The key used while there is nothing to fetch.
 *
 * React Query needs a key on every render, hooks cannot be called
 * conditionally, and a disabled query still registers an (empty) cache entry.
 * A three-element key is structurally distinct from the five-element `detail`
 * key, so an idle entry can never be mistaken for — or invalidated as — a real
 * partition.
 */
const IDLE_QUERY_KEY = ['stories', 'briefing-context', '__idle__'] as const

// ---------------------------------------------------------------------------
// Local token digest — NOT exported (R31)
// ---------------------------------------------------------------------------

function fnv1a(input: string, basis: number): number {
  let hash = basis >>> 0
  for (let i = 0; i < input.length; i += 1) {
    const code = input.charCodeAt(i)
    hash = Math.imul(hash ^ (code & 0xff), 0x01000193) >>> 0
    hash = Math.imul(hash ^ (code >>> 8), 0x01000193) >>> 0
  }
  return hash >>> 0
}

/**
 * A fixed-width, one-way, purely local partition label for an opaque token.
 * Never exported, never transmitted, never logged.
 */
function digestBriefingContextToken(token: string): string {
  const low = fnv1a(token, 0x811c9dc5)
  const high = fnv1a(token, 0x9e3779b9)
  return `${high.toString(16).padStart(8, '0')}${low.toString(16).padStart(8, '0')}`
}

// ---------------------------------------------------------------------------
// Lifetime bounds (R33)
// ---------------------------------------------------------------------------

/**
 * The window this response may be cached for.
 *
 * The browser cannot read the token's own expiry — verification needs the
 * purpose-scoped key, which never leaves the server. What it does have is the
 * bootstrap's LOCAL continuation deadline, which U4 sets strictly tighter than
 * any token lifetime. Bounding by it therefore satisfies "cache lifetime cannot
 * exceed the token's remaining validity" from the strict side.
 *
 * Exported for direct testing; the hook is its only production caller.
 */
export function briefingContextCacheBounds(
  expiresAt: number,
  now: number = Date.now(),
): { staleTime: number; gcTime: number } {
  const remaining = Math.max(0, expiresAt - now)
  return { staleTime: remaining, gcTime: remaining }
}

// ---------------------------------------------------------------------------
// Transport
// ---------------------------------------------------------------------------

function isMatch(value: unknown): value is BriefingCurrentMatch {
  if (typeof value !== 'object' || value === null) return false
  const candidate = value as Record<string, unknown>
  return (
    typeof candidate.displayValue === 'string' &&
    MATCH_REASONS.includes(candidate.reason as BriefingMatchReason)
  )
}

/**
 * A shape check, not a trust decision: the server is the authority on what a
 * context contains, and this only stops a surprising body (a proxy error page,
 * a future field rename) from reaching the renderer as a half-object.
 */
function asContext(value: unknown): BriefingStoryContext | null {
  if (typeof value !== 'object' || value === null) return null
  const candidate = value as Record<string, unknown>
  if (typeof candidate.articleId !== 'string') return null
  const actions = Array.isArray(candidate.suggestedActions)
    ? candidate.suggestedActions.filter((a): a is string => typeof a === 'string')
    : []
  const matches = Array.isArray(candidate.currentMatches)
    ? candidate.currentMatches.filter(isMatch)
    : []
  return { articleId: candidate.articleId, suggestedActions: actions, currentMatches: matches }
}

/**
 * `POST /stories/:id/briefing-context` with the token in the BODY.
 *
 * A GET would put the token in `req.url`, which pino-http, Sentry, the load
 * balancer and the browser's own `Referer` all see before any handler runs
 * (R14, R20). `cache: 'no-store'` is the client half of R35 — the server sends
 * `private, no-store`, and this makes the request itself ineligible for the HTTP
 * cache regardless of what any intermediary decides to do with the response.
 *
 * Written with `fetch` rather than `api.post` for one reason: the shared client
 * has no way to pass an `AbortSignal`, and R32's ordering guarantee is built on
 * cancellation.
 */
async function requestBriefingContext(
  storyId: string,
  token: string,
  signal: AbortSignal,
): Promise<BriefingStoryContextResponse> {
  const response = await fetch(`${API_BASE_URL}/stories/${storyId}/briefing-context`, {
    method: 'POST',
    credentials: 'include',
    cache: 'no-store',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ token }),
    signal,
  })
  // Every non-2xx is an ordinary outcome here, not an error to surface: an
  // expired session, a rotated key or a re-clustered article must all land the
  // reader on the ordinary story page (F4, R41).
  if (!response.ok) return { context: null }
  try {
    const body = (await response.json()) as { context?: unknown }
    return { context: asContext(body?.context) }
  } catch {
    return { context: null }
  }
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

interface ClaimedContext {
  /**
   * The story this token was claimed FOR.
   *
   * Load-bearing, not bookkeeping. React renders before it runs effects, so on a
   * navigation from story A to story B there is one render in which `storyId` is
   * already B while this state still holds A's claim. Without this field that
   * render builds a live query key for B out of A's token and posts A's token to
   * B's endpoint — a cross-story transmission of a bearer token that the server
   * would rightly reject, but that should never have left the browser. Pinning
   * the claim to its story makes that render inert instead.
   */
  storyId: string
  token: string
  digest: string
  expiresAt: number
}

export interface UseBriefingStoryContextOptions {
  storyId: string
  /**
   * The current auth subject (`user.sub`), or null/undefined while unresolved.
   *
   * Passed in rather than read from `useAuth()` so this module stays free of the
   * auth/UI import graph — `hooks/queries.ts` imports the key registry from here
   * and must not drag a React context provider in with it.
   */
  subject: string | null | undefined
}

export interface UseBriefingStoryContextResult {
  context: BriefingStoryContext | null
  isLoading: boolean
}

export function useBriefingStoryContext({
  storyId,
  subject,
}: UseBriefingStoryContextOptions): UseBriefingStoryContextResult {
  const queryClient = useQueryClient()
  const [claimed, setClaimed] = useState<ClaimedContext | null>(null)

  // KAN-708. `consumePendingBriefingContext` is DESTRUCTIVE — it reads the
  // pending record and clears `sessionStorage` in the same call, so it answers
  // exactly once. The app mounts inside `<React.StrictMode>` (`main.tsx`),
  // which in development runs every mount effect, cleans it up, and runs it
  // AGAIN. Without a guard the first invocation consumed the record and the
  // second, running against an already-empty store, got `null` and reset the
  // claim — so the panel never appeared and the continuation was gone, with no
  // reload able to recover it.
  //
  // The guard has to be a ref, not a `claimed` check: `setClaimed` is
  // asynchronous, so both StrictMode invocations observe the same pre-update
  // `claimed` and a state check would let both through. It is KEYED rather
  // than a one-shot latch so a genuine change of story or subject still
  // consumes that arrival's own record — the re-key IS the re-run.
  const consumedKeyRef = useRef<string | null>(null)

  // The single consumption point (F5 step 4). It runs only once a subject has
  // resolved: consuming while signed out would burn the continuation on the
  // outbound leg of the OIDC round trip, which is exactly what F5 exists to
  // prevent. A change of `storyId` re-runs it, and U4 treats a story mismatch as
  // terminal — navigating away from the linked story ends the continuation
  // rather than parking it for a later visit (R38).
  useEffect(() => {
    if (!storyId || !subject) {
      // Nothing was consumed on this pass, so nothing is claimed against a
      // key either — a later sign-in must be free to consume.
      consumedKeyRef.current = null
      setClaimed(null)
      return
    }
    // NUL-separated: neither a ZITADEL subject nor a UUID can contain it, so
    // no two distinct (subject, storyId) pairs can collide on one key.
    const consumeKey = `${subject}\u0000${storyId}`
    if (consumedKeyRef.current === consumeKey) return
    consumedKeyRef.current = consumeKey

    const pending = consumePendingBriefingContext(storyId)
    setClaimed(
      pending
        ? {
            storyId,
            token: pending.token,
            digest: digestBriefingContextToken(pending.token),
            expiresAt: pending.expiresAt,
          }
        : null,
    )
  }, [storyId, subject])

  // R32's cache half. Cancellation is U4's and runs first; this only supplies
  // the removal that follows it. Dropping the local claim as well is what makes
  // the panel disappear in the same tick rather than at the next navigation.
  const resetCache = useCallback(() => {
    queryClient.removeQueries({ queryKey: briefingStoryContextQueryKeys.all })
    setClaimed(null)
  }, [queryClient])

  useEffect(() => registerBriefingContextCacheReset(resetCache), [resetCache])

  // See `ClaimedContext.storyId`: a claim is only live on the story it was
  // claimed for. Everywhere below reads `active`, never `claimed`.
  const active = claimed && claimed.storyId === storyId ? claimed : null

  // R33 — the deadline is ENFORCED, not merely declared.
  //
  // `staleTime`/`gcTime` below bound the lifetime of an UNUSED query. React
  // Query does not evict a query an observer is still watching, and neither
  // option is a timer that fires while the panel is on screen. Without this,
  // a reader who opens a briefing link and leaves the tab open keeps the exact
  // match values, the suggested actions and the clicked article id in live
  // query data — and rendered — for as long as the tab lives, which is exactly
  // what "no persistence beyond the active page/session" forbids.
  //
  // One scheduled timeout per claim, not a poll: the instant is already known,
  // so there is nothing to sample for. `active` is either the `claimed` state
  // object or `null`, so its identity is stable between claims and the cleanup
  // below runs exactly once per claim — on story change, on a new claim, and on
  // unmount. A claim that is already past its deadline is dropped on the spot
  // rather than scheduled into the past.
  //
  // This does not touch U4's teardown ordering: `endBriefingContextSession`
  // still cancels in flight before invoking the registered reset. Expiry is a
  // separate path that ends the continuation locally, and `enabled` goes false
  // in the same tick, so nothing refetches behind it.
  useEffect(() => {
    if (!active) return
    const remaining = active.expiresAt - Date.now()
    if (remaining <= 0) {
      resetCache()
      return
    }
    const timer = setTimeout(resetCache, remaining)
    return () => clearTimeout(timer)
  }, [active, resetCache])

  const bounds = useMemo(
    () => (active ? briefingContextCacheBounds(active.expiresAt) : null),
    [active],
  )

  const enabled = Boolean(active && subject && bounds && bounds.gcTime > 0)

  const query = useQuery<BriefingStoryContextResponse>({
    queryKey:
      active && subject
        ? briefingStoryContextQueryKeys.detail(subject, storyId, active.digest)
        : IDLE_QUERY_KEY,
    queryFn: async () => {
      const outcome = await runBriefingContextRequest((signal) =>
        requestBriefingContext(storyId, active!.token, signal),
      )
      // `null` means the request was cancelled in flight. Collapsing it to an
      // empty context rather than throwing keeps a torn-down session quiet.
      return outcome ?? { context: null }
    },
    enabled,
    staleTime: bounds?.staleTime ?? 0,
    gcTime: bounds?.gcTime ?? 0,
    retry: false,
    refetchOnWindowFocus: false,
  })

  return {
    context: enabled ? (query.data?.context ?? null) : null,
    isLoading: enabled && query.isPending,
  }
}
